import * as React from 'react';
import { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectDisplayTax } from '@components/Preferences/preferencesSlice';
import imageService from '@services/image-service';
import { selectAlternativeProduct } from '@components/ProductDetail/state/productDetailSlice';
import { addToCart } from '@components-react/Cart/cartSlice';
import { useApi } from '@hooks/use-api';
import styles from './AlternativeProduct.module.scss';
import type { CartItem } from '@sctypes/product/CartItem';

interface FormattedPrices {
  itemPrice: string;
  exTax: string;
  incTax: string;
}


export const AlternativeProduct = () => {
  const dispatch = useDispatch();
  const alternativeProductCode = useSelector(state => selectAlternativeProduct(state));

  const [{ data: product, error: fetchError, isLoading }, fetchProduct] = useApi.get(
    alternativeProductCode?.length > 0 ? `/products/${alternativeProductCode[0]}` : null,
    {},
    { lazy: true }
  );

  const displayTax = useSelector(state => selectDisplayTax(state));
  const taxKey = displayTax ? 'incTax' : 'exTax';
  const variant = product?.variants?.find((variant: any) => variant.code === product.defaultVariantCode);
  const imageUrl =
    variant?.media.find((media: any) => media.type === 'Image' && media.thumbnail !== '')?.thumbnail ??
    variant?.media.find((media: any) => media.type === 'Image')?.src;

  const thumbnail = imageService.getUrl(imageUrl, 80, 80, true);
  const price = variant?.unitsOfMeasure[0]?.prices.current[taxKey];

  // Add formatPrices function similar to ProductTile
  const formatPrices = useCallback((price: any, itemsPerUnit = 1): FormattedPrices => {
    const incTax = price?.incTax?.numeric;
    const exTax = price?.exTax?.numeric;

    if (!incTax || !exTax) {
      return {
        itemPrice: '--',
        exTax: '--',
        incTax: '--'
      };
    }

    const itemPrice: number = displayTax
      ? (incTax / itemsPerUnit)
      : (exTax / itemsPerUnit);

    return {
      itemPrice: new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(itemPrice),
      exTax: price.exTax.formatted,
      incTax: price.incTax.formatted
    };
  }, [displayTax]);

  // Calculate price description with same logic as ProductTile
  const priceDescription = useMemo(() => {
    const unitOfMeasure = variant?.unitsOfMeasure[0];
    if (!unitOfMeasure?.description || !variant?.unitsOfMeasure[0]?.prices?.current) {
      return unitOfMeasure?.description || '';
    }

    if (unitOfMeasure.itemsPerUnit > 1) {
      const { itemPrice }: FormattedPrices = formatPrices(variant.unitsOfMeasure[0].prices.current, unitOfMeasure.itemsPerUnit);
      return unitOfMeasure.description.replace('#PRICE#', itemPrice);
    }

    return unitOfMeasure.description;
  }, [variant, formatPrices]);

  useEffect(() => {
    // Only run on client side (not during SSR)
    if (!global.window || !alternativeProductCode?.length) {
      return;
    }

    // Trigger the API request
    fetchProduct();
  }, [alternativeProductCode, fetchProduct]);

  const handleAddToCart = () => {
    const unitOfMeasure = variant.unitsOfMeasure[0];

    const cartItem: CartItem[] = [{
      variantCode: variant.code,
      orderAmount: 1,
      unitOfMeasureCode: unitOfMeasure.code,
      currentVariant: variant
    }];
    addToCart(dispatch)(cartItem);
  };


  if (!product || fetchError || isLoading) {
    return null;
  }

  return (
    <div className={`${styles.alternativeProduct} row mb-3`}>
      <div className="row mb-3">
        <div className="col">
          <h2 className="h3 text-theme">{'Besparen? Kies voor Premium A+'}</h2>
        </div>
      </div>
      <div className="row">
        <div className="col-auto">
          <div className={styles.image}>
            <img src={thumbnail} width={80} height={80} alt={`Thumbnail image of ${variant.title ?? product.title}`} />
          </div>
        </div>
        <div className="col">
          <h3 className="h3">{variant.title}</h3>
          <span className="text-muted">Art.: {variant.code}</span>
          <div>
            <div>
              <span className={styles.price}>{price?.formatted}</span>
              <span className={styles.priceDescription}>{priceDescription}</span>
            </div>
            <div>
              <span className="text-muted text-xs text-nowrap">{displayTax ? 'Incl. BTW' : 'Excl. BTW'}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="row w-100 justify-content-end">
        <div className="">
          <button className="btn btn-cart btn-block p-2 px-3" onClick={handleAddToCart}>
            <i className="fa cl-cart fa-lg"></i> In winkelwagen
          </button>
        </div>
      </div>
    </div>
  );
};
